import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";

import {
  KHeaderWidgetLogo,
  KHeaderWidgetLogoPosition,
  KHeaderWidget as KHeaderWidgetModel,
} from "../../../models/configuration/components/kHeaderWidget";

const PREFIX = "KHeaderWidget";

export const classes = {
  root: `${PREFIX}-root`,
  logoTitle: `${PREFIX}-logoTitle`,
  logo: `${PREFIX}-logo`,
  button: `${PREFIX}-button`,
  nav: `${PREFIX}-nav`,
  logoBox: `${PREFIX}-logoBox`,
  title: `${PREFIX}-title`,
  actionsBox: `${PREFIX}-actionsBox`,
  basketBox: `${PREFIX}-basketBox`,
  burger: `${PREFIX}-burger`,
  burgerIconButton: `${PREFIX}-burgerIconButton`,
};

const StyledKHeaderWidget = styled(Box)<
  Pick<KHeaderWidgetModel, "backgroundColor"> & {
    logoPosition: KHeaderWidgetLogo["kHeaderWidgetLogoPosition"];
    barWidth?: number;
  }
>(({ theme, backgroundColor, logoPosition, barWidth }) => ({
  [`&.${classes.root}`]: {
    width: "100%",
    position: "relative",
    zIndex: theme.zIndex.appBar,
  },

  [`& .${classes.logoTitle}`]: {
    padding: 0,
    margin: 0,
    height: "3rem",
    maxHeight: "8vh",
    maxWidth: "40vw",
    position: "relative",
  },

  [`& .${classes.logo}`]: {
    maxHeight: "100%",
    maxWidth: "100%",
  },

  [`& .${classes.button}`]: {
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.text.primary,
    alignSelf: "stretch",
    "& + &": {
      marginLeft: theme.spacing(2),
    },
    "&:focus-within": {
      outline: `1px solid ${theme.palette.primary.main}`,
    },
  },

  [`& .${classes.nav}`]: {
    display: "grid",
    gap: "1rem",
    paddingInline: theme.spacing(1),
    gridTemplateAreas: getNavGridTemplateAreas({ logoPosition }),
    gridTemplateColumns: getNavGridTemplateColumns({ theme, logoPosition }),
    backgroundColor: backgroundColor ? backgroundColor : "white",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
    },
  },

  [`& .${classes.logoBox}`]: {
    gridArea: "logo",
    padding: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      flexGrow: 1,
      justifyContent: "center",
    },
  },

  [`& .${classes.title}`]: {
    fontSize: theme.spacing(2.5),
    color: theme.palette.primary.main,
    marginLeft: theme.spacing(4),
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  },

  [`& .${classes.actionsBox}`]: {
    gridArea: "actions",
    display: "flex",
    alignItems: "center",
    paddingLeft: theme.spacing(1),
    justifyContent: getActionsBoxJustiyContent({ logoPosition }),
    overflow: "auto",
    ...(barWidth &&
      barWidth < theme.breakpoints.values.sm && {
        display: "none",
      }),
  },

  [`& .${classes.burger}`]: {
    gridArea: "burger",
    display: "flex",
    alignItems: "center",
  },

  [`& .${classes.burgerIconButton}`]: {},

  [`& .${classes.basketBox}`]: {
    gridArea: "basket",
    display: "flex",
    alignItems: "center",
  },
  // Due to a TS limitation, we need to override the type here (see: https://github.com/mui-org/material-ui/issues/15759#issuecomment-493994852) :
}));

function getNavGridTemplateAreas({ logoPosition }) {
  switch (logoPosition) {
    case KHeaderWidgetLogoPosition.Center:
      // Create two empty area on the left to balance actions and basket
      return `"burger . logo actions basket"`;
    case KHeaderWidgetLogoPosition.Right:
      return `"burger actions logo basket"`;
    case KHeaderWidgetLogoPosition.Left:
    default:
      return `"burger logo actions basket"`;
  }
}

function getNavGridTemplateColumns({ theme, logoPosition }) {
  switch (logoPosition) {
    case KHeaderWidgetLogoPosition.Center:
      // Create symetrical spaces between left and right around logo
      return `${theme.spacing(6)} 1fr max-content 1fr ${theme.spacing(6)}`;
    case KHeaderWidgetLogoPosition.Right:
      return "min-content 1fr max-content max-content";
    case KHeaderWidgetLogoPosition.Left:
    default:
      return "min-content max-content 1fr max-content";
  }
}

function getActionsBoxJustiyContent({ logoPosition }) {
  switch (logoPosition) {
    case KHeaderWidgetLogoPosition.Center:
      return "flex-end";
    case KHeaderWidgetLogoPosition.Right:
      return "flex-start";
    case KHeaderWidgetLogoPosition.Left:
    default:
      return "flex-end";
  }
}

export default StyledKHeaderWidget;
